import posthog from 'posthog-js'
import { createConsentSafeCapture, isInternalAnalyticsUser } from '../../../../shared-types/posthog-privacy'
import { hasAnalyticsConsent } from './cookie-consent'

export type ReportCtaPlacement = 'article-inline' | 'article-footer'

export interface ReportCtaIdentity {
	loading: boolean
	user: { userId: string; role?: unknown } | null
}

interface ReportCtaClick {
	href: string
	postSlug: string
	placement: ReportCtaPlacement
}

/** A click is navigation intent, never proof that the destination report loaded. */
export function createBlogReportCtaCapture(readIdentity: () => ReportCtaIdentity) {
	const capture = createConsentSafeCapture({
		hasConsent: hasAnalyticsConsent,
		isReady: () => posthog.__loaded,
		send: (event, properties, timestamp) => {
			const { loading, user } = readIdentity()
			if (loading) return
			if (user?.userId) {
				const isInternal = isInternalAnalyticsUser(user)
				posthog.register({ is_internal: isInternal })
				if (posthog.get_distinct_id() !== user.userId) {
					posthog.identify(user.userId, { role: user.role, is_internal: isInternal })
				}
			} else {
				// An expired app session may leave a persisted identified SDK user.
				// Keep genuine anonymous IDs stable across separate article clicks.
				const previousUserId = posthog.get_property('$user_id')
				const previouslyIdentified =
					posthog.get_property('$user_state') === 'identified' ||
					(typeof previousUserId === 'string' && previousUserId.length > 0)
				if (previouslyIdentified) posthog.reset()
				if (!hasAnalyticsConsent()) return
				posthog.register({ is_internal: false })
			}
			posthog.capture(event, properties, { timestamp })
		},
	})

	return ({ href, postSlug, placement }: ReportCtaClick): void => {
		try {
			if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(postSlug) || postSlug.length > 200) return
			if (placement !== 'article-inline' && placement !== 'article-footer') return
			const url = new URL(href)
			if (url.origin !== 'https://catalog.extenshi.io' || url.username || url.password) return
			const match = /^\/extensions\/([1-9]\d*)\/security\/?$/.exec(url.pathname)
			if (!match || !Number.isSafeInteger(Number(match[1]))) return
			capture('blog_report_cta_clicked', {
				schema_version: 2,
				post_slug: postSlug,
				extension_id: match[1],
				placement,
				destination_path: `/extensions/${match[1]}/security`,
			})
		} catch {
			// URL, identity or telemetry failures must never delay native navigation.
		}
	}
}
